import type { GlobalCollectionId, GlobalFieldId, GlobalPrimitiveFieldId, GlobalSingleRelationFieldId } from '#layers/data-model/types/schema/ids';
import type { PrimitiveType } from '#layers/data-model/types/schema/primitive-type';
import type { CollectionFieldPathGraph, CollectionFieldPathGraphQuery } from '#layers/data-model/utils/collection-field-path-graph';
import type { FlattenedItem, NestedSelectMenuItem } from '#layers/ui/public';
import { AcceptedPathResult, createCollectionFieldPathGraph, FieldPathMatchMode, fieldPathMatchPolicies, findAcceptedPath } from '#layers/data-model/utils/collection-field-path-graph';
import { normalizeCollectionFieldPathQuery, searchCollectionFieldPathItems } from '#layers/data-model/utils/collection-field-path-matching';
import { getPrimitiveTypeIcon } from '#layers/data-model/utils/icons';

/** Default traversal depth for field paths — the build/walk cap and the menu's default max nesting. */
export const DEFAULT_FIELD_PATH_MAX_DEPTH = 15;

export type CollectionFieldPath = [
	...GlobalSingleRelationFieldId[],
	GlobalPrimitiveFieldId | GlobalSingleRelationFieldId,
];

export type PrimitiveFieldPath = [
	...GlobalSingleRelationFieldId[],
	GlobalPrimitiveFieldId,
];

export type CollectionFieldPathTerminal = 'primitive' | 'primitive-or-single-relation' | 'primitive-or-relation';

export type CollectionFieldPathForTerminal<TTerminal extends CollectionFieldPathTerminal | undefined> =
	TTerminal extends 'primitive-or-single-relation' | 'primitive-or-relation'
		? CollectionFieldPath
		: PrimitiveFieldPath;

export interface CollectionFieldPathPrimitiveField {
	id: GlobalPrimitiveFieldId;
	apiName: string;
	type: PrimitiveType;
	isStudioOnly?: boolean;
	meta?: { displayName?: string | null } | null;
	constrainedBy?: unknown[] | null;
}

export interface CollectionFieldPathRelationField {
	id: GlobalSingleRelationFieldId;
	apiName: string;
	isList: boolean;
	isStudioOnly?: boolean;
	meta?: { displayName?: string | null } | null;
	oppositeCollection?: CollectionFieldPathCollection | null;
}

export type CollectionFieldPathField = CollectionFieldPathPrimitiveField | CollectionFieldPathRelationField;

export interface CollectionFieldPathCollection {
	id: GlobalCollectionId;
	isSystem?: boolean;
	primitiveFields: readonly CollectionFieldPathPrimitiveField[];
	relationFields?: readonly CollectionFieldPathRelationField[];
}

/**
 * Return false to exclude a field from every path. Excluding a relation also excludes all of its
 * descendants, so their collections are never read.
 */
export type CollectionFieldPathIncludeField = (collection: CollectionFieldPathCollection, field: CollectionFieldPathField) => boolean;

export interface CollectionFieldPathBuildOptions {
	includePrimitiveFields?: boolean;
	includeConstrainedFields?: boolean;
	includeRelationFields?: boolean;
	includeListRelationFields?: boolean;
	includeListRelationTerminalFields?: boolean;
	includeSystemCollections?: boolean;
	includeField?: CollectionFieldPathIncludeField;
	label?: 'displayName' | 'apiName';
	terminal?: CollectionFieldPathTerminal;
	resolveRelationCollection?: (fieldId: GlobalSingleRelationFieldId) => CollectionFieldPathCollection | undefined;
	getFieldDisplayName: (field: CollectionFieldPathField) => string;
	/**
	 * When set, hide rejected selectable paths and relations without an accepted descendant.
	 * Descendant levels are still built only when expanded.
	 */
	filterFieldPath?: (fieldPath: CollectionFieldPath) => boolean;
	/** Maximum depth available when expanding filtered relation paths. Defaults to 15. */
	maxDepth?: number;
}

interface ResolvedCollectionFieldPathBuildOptions extends
	Required<Pick<CollectionFieldPathBuildOptions, 'includePrimitiveFields' | 'includeConstrainedFields' | 'includeRelationFields' | 'includeListRelationFields'
	| 'includeListRelationTerminalFields' | 'includeSystemCollections' | 'label' | 'terminal' | 'getFieldDisplayName' | 'maxDepth'>>,
	Pick<CollectionFieldPathBuildOptions, 'resolveRelationCollection' | 'includeField'> {
	filter?: CollectionFieldPathFilter;
}

/** A path filter paired with the unfiltered graph used to find accepted descendants. */
interface CollectionFieldPathFilter {
	accepts: (fieldPath: CollectionFieldPath) => boolean;
	graph: CollectionFieldPathGraph;
}

export interface CollectionFieldPathResolveOptions<TTerminal extends CollectionFieldPathTerminal | undefined = 'primitive'> {
	includePrimitiveFields?: boolean;
	includeConstrainedFields?: boolean;
	includeRelationFields?: boolean;
	includeListRelationFields?: boolean;
	includeListRelationTerminalFields?: boolean;
	includeSystemCollections?: boolean;
	includeField?: CollectionFieldPathIncludeField;
	terminal?: TTerminal;
	resolveRelationCollection?: (fieldId: GlobalSingleRelationFieldId) => CollectionFieldPathCollection | undefined;
}

export interface CollectionFieldPathListOptions<TTerminal extends CollectionFieldPathTerminal | undefined = CollectionFieldPathTerminal> {
	includePrimitiveFields?: boolean;
	includeConstrainedFields?: boolean;
	includeListRelationFields?: boolean;
	includeListRelationTerminalFields?: boolean;
	includeSystemCollections?: boolean;
	includeField?: CollectionFieldPathIncludeField;
	terminal?: TTerminal;
	maxDepth?: number;
}

export type CollectionFieldPathItemKind = 'primitive' | 'single-relation' | 'list-relation';

export interface CollectionFieldPathItem extends NestedSelectMenuItem {
	id: GlobalFieldId;
	apiName: string;
	displayName: string;
	fieldPathKind: CollectionFieldPathItemKind;
	searchText?: string;
	children?: CollectionFieldPathItem[] | (() => CollectionFieldPathItem[]);
}

export interface CollectionFieldPathFlattenedItem extends FlattenedItem {
	item: CollectionFieldPathItem;
	path: CollectionFieldPathItem[];
}

export interface ResolvedCollectionFieldPathSegment {
	collection: CollectionFieldPathCollection;
	field: CollectionFieldPathField;
}

function terminalAllowsSingleRelation(terminal: CollectionFieldPathTerminal): boolean {
	return terminal === 'primitive-or-single-relation' || terminal === 'primitive-or-relation';
}

function terminalAllowsListRelation(terminal: CollectionFieldPathTerminal): boolean {
	return terminal === 'primitive-or-relation';
}

function isRelationSelectableTerminal(
	field: Pick<CollectionFieldPathRelationField, 'isList'>,
	options: { terminal: CollectionFieldPathTerminal; includeListRelationTerminalFields: boolean },
): boolean {
	return field.isList
		? terminalAllowsListRelation(options.terminal) && options.includeListRelationTerminalFields
		: terminalAllowsSingleRelation(options.terminal);
}

function isRelationTraversable(
	field: Pick<CollectionFieldPathRelationField, 'isList' | 'isStudioOnly'>,
	options: { includeListRelationFields: boolean },
): boolean {
	if (field.isStudioOnly) return false; // presentation alias the Engine cannot query through
	return !field.isList || options.includeListRelationFields;
}

function isSystemCollection(collection: CollectionFieldPathCollection | null | undefined): boolean {
	return collection?.isSystem === true;
}

/** The relation's target collection — embedded on the field, or resolved via the option callback. */
function resolveOppositeCollection(
	field: Pick<CollectionFieldPathRelationField, 'id' | 'oppositeCollection'>,
	options: { resolveRelationCollection?: (fieldId: GlobalSingleRelationFieldId) => CollectionFieldPathCollection | undefined },
): CollectionFieldPathCollection | undefined {
	return field.oppositeCollection ?? options.resolveRelationCollection?.(field.id);
}

/** Defaults the field-inclusion flags shared by every field-path walker (terminal-list defaults to list). */
function resolveIncludeFlags(options: {
	includePrimitiveFields?: boolean;
	includeConstrainedFields?: boolean;
	includeListRelationFields?: boolean;
	includeListRelationTerminalFields?: boolean;
}) {
	const includePrimitiveFields = options.includePrimitiveFields ?? true;
	const includeConstrainedFields = options.includeConstrainedFields ?? false;
	const includeListRelationFields = options.includeListRelationFields ?? false;
	const includeListRelationTerminalFields = options.includeListRelationTerminalFields ?? includeListRelationFields;
	return { includePrimitiveFields, includeConstrainedFields, includeListRelationFields, includeListRelationTerminalFields };
}

function getFieldLabel(
	field: CollectionFieldPathField,
	options: Required<Pick<CollectionFieldPathBuildOptions, 'label' | 'getFieldDisplayName'>>,
): string {
	if (options.label === 'apiName') return field.apiName;
	return options.getFieldDisplayName(field);
}

function getFieldSearchText(
	field: CollectionFieldPathField,
	getFieldDisplayName: CollectionFieldPathBuildOptions['getFieldDisplayName'],
): string {
	const displayLabel = getFieldDisplayName(field);

	return [displayLabel, field.apiName]
		.filter((value, index, values) => values.indexOf(value) === index)
		.join(' ');
}

function buildPrimitiveFieldItem(
	field: CollectionFieldPathPrimitiveField,
	options: Required<Pick<CollectionFieldPathBuildOptions, 'label' | 'getFieldDisplayName'>>,
): CollectionFieldPathItem {
	return {
		id: field.id,
		apiName: field.apiName,
		displayName: options.getFieldDisplayName(field),
		fieldPathKind: 'primitive',
		label: getFieldLabel(field, options),
		icon: getPrimitiveTypeIcon(field.type),
		searchText: getFieldSearchText(field, options.getFieldDisplayName),
	};
}

/**
 * Builds the menu item for a single relation field — a branch into its opposite collection.
 *
 * Without `filterFieldPath` the item carries a lazy `children` thunk, so the nested menu expands
 * the opposite collection only when the user drills in (this is what keeps self-referential
 * relations traversable without materializing an infinite tree).
 *
 * With `filterFieldPath`, terminal selection is filtered at the current level and descendants
 * are filtered when the menu expands the relation. A relation that is not itself selectable is
 * kept only if the graph finds an accepted descendant, so dead branches do not render.
 *
 * Returns `null` when the relation is rejected as a terminal and has no accepted descendant.
 */
function buildRelationFieldItem(
	field: CollectionFieldPathRelationField,
	options: ResolvedCollectionFieldPathBuildOptions,
	prefix: GlobalSingleRelationFieldId[],
): CollectionFieldPathItem | null {
	if (!options.includeRelationFields) return null;
	if (field.isList && !options.includeListRelationFields && !options.includeListRelationTerminalFields) return null;

	const oppositeCollection = resolveOppositeCollection(field, options);

	if (!options.includeSystemCollections && isSystemCollection(oppositeCollection)) return null;

	if (!oppositeCollection && (!field.isList || options.includeListRelationFields)) return null;

	const traversable = oppositeCollection && isRelationTraversable(field, options)
		? oppositeCollection
		: undefined;

	const selectable = isRelationSelectableTerminal(field, options);

	if (!traversable && !selectable) return null;

	const item: CollectionFieldPathItem = {
		id: field.id,
		apiName: field.apiName,
		displayName: options.getFieldDisplayName(field),
		fieldPathKind: field.isList ? 'list-relation' : 'single-relation',
		label: getFieldLabel(field, options),
		icon: field.isList ? 'lucide:list' : 'lucide:link',
		searchText: getFieldSearchText(field, options.getFieldDisplayName),
		selectable,
	};

	const { filter } = options;

	if (!filter) {
		return traversable
			? { ...item, children: () => buildCollectionFieldPathItemsInternal(traversable, options) }
			: item;
	}

	const relationPath: GlobalSingleRelationFieldId[] = [...prefix, field.id];
	const path: CollectionFieldPath = [...prefix, field.id];
	const keepAsTerminal = (!traversable || selectable) && filter.accepts(path) !== false;

	if (!traversable || path.length >= options.maxDepth) {
		return keepAsTerminal ? item : null;
	}

	if (!keepAsTerminal && findAcceptedPath(filter.graph, filter.accepts, traversable, relationPath, options.maxDepth - path.length - 1) === AcceptedPathResult.None) {
		return null;
	}

	return {
		...item,
		selectable: keepAsTerminal,
		// Expansion runs in its own reactive evaluation. Reusing the parent's graph would retain
		// stale nodes and skip tracking descendant schema/permission reads on subsequent evaluations.
		children: () => buildCollectionFieldPathItemsInternal(traversable, {
			...options,
			filter: { ...filter, graph: createUnfilteredGraph(options) },
		}, relationPath),
	};
}

function sortItems(items: CollectionFieldPathItem[]): CollectionFieldPathItem[] {
	return items.toSorted((left, right) => left.label.localeCompare(right.label));
}

/**
 * Recursive worker that builds one menu level: primitive leaves first, then relation branches.
 * `prefix` is the path of relation fields taken to reach this level (used to build the path passed
 * to `filterFieldPath`).
 */
function buildCollectionFieldPathItemsInternal(
	collection: CollectionFieldPathCollection,
	options: ResolvedCollectionFieldPathBuildOptions,
	prefix: GlobalSingleRelationFieldId[] = [],
): CollectionFieldPathItem[] {
	const items: CollectionFieldPathItem[] = [];

	if (options.includePrimitiveFields) {
		for (const field of collection.primitiveFields) {
			if (options.includeField?.(collection, field) === false) continue;
			if (!options.includeConstrainedFields && isConstrainedPrimitiveField(field)) continue;
			if (options.filter?.accepts([...prefix, field.id] as CollectionFieldPath) === false) continue;
			items.push(buildPrimitiveFieldItem(field, options));
		}
	}

	if (options.includeRelationFields) {
		for (const field of collection.relationFields ?? []) {
			if (options.includeField?.(collection, field) === false) continue;
			const relationItem = buildRelationFieldItem(field, options, prefix);

			if (relationItem) {
				items.push(relationItem);
			}
		}
	}

	return sortItems(items);
}

/**
 * Builds the nested field-path menu tree that `CollectionFieldPathSelect` feeds to `DNestedSelectMenu`.
 *
 * Every collection level lists primitive fields as selectable leaves and single/list relations as
 * branches into their opposite collection. Relation `children` are lazy thunks the menu forces as
 * the user expands, so self-referential relations stay traversable without building an infinite tree.
 *
 * Passing `filterFieldPath` hides rejected selectable paths and dead relation branches at each
 * level. Descendants remain lazy, and `maxDepth` bounds expansion through cyclic schemas (default 15).
 */
export function buildCollectionFieldPathItems(
	collection: CollectionFieldPathCollection,
	options: CollectionFieldPathBuildOptions,
): CollectionFieldPathItem[] {
	return buildCollectionFieldPathItemsInternal(collection, resolveBuildOptions(options));
}

function resolveBuildOptions(options: CollectionFieldPathBuildOptions): ResolvedCollectionFieldPathBuildOptions {
	const resolved: ResolvedCollectionFieldPathBuildOptions = {
		...resolveIncludeFlags(options),
		includeRelationFields: options.includeRelationFields ?? true,
		includeSystemCollections: options.includeSystemCollections ?? true,
		label: options.label ?? 'displayName',
		terminal: options.terminal ?? 'primitive',
		maxDepth: options.maxDepth ?? DEFAULT_FIELD_PATH_MAX_DEPTH,
		resolveRelationCollection: options.resolveRelationCollection,
		includeField: options.includeField,
		getFieldDisplayName: options.getFieldDisplayName,
	};

	return options.filterFieldPath
		? { ...resolved, filter: { accepts: options.filterFieldPath, graph: createUnfilteredGraph(resolved) } }
		: resolved;
}

/** A graph over unfiltered menu levels, used to prune filtered branches and to search paths. */
function createUnfilteredGraph(options: ResolvedCollectionFieldPathBuildOptions): CollectionFieldPathGraph {
	const levelOptions: ResolvedCollectionFieldPathBuildOptions = { ...options, filter: undefined };

	return createCollectionFieldPathGraph((collection) => {
		const relations = new Map<GlobalFieldId, CollectionFieldPathRelationField>(collection.relationFields?.map((field) => [field.id, field]));

		return buildCollectionFieldPathItemsInternal(collection, levelOptions).map((item) => {
			const relation = item.children ? relations.get(item.id) : undefined;
			const target = relation ? resolveOppositeCollection(relation, levelOptions) : undefined;
			return { item, relation: relation && target ? { id: relation.id, target } : undefined };
		});
	});
}

function isConstrainedPrimitiveField(field: CollectionFieldPathPrimitiveField): boolean {
	return Array.isArray(field.constrainedBy) && field.constrainedBy.length > 0;
}

function walkCollectionFieldPath<TTerminal extends CollectionFieldPathTerminal | undefined = 'primitive'>(
	collection: CollectionFieldPathCollection,
	selectedPath: string[],
	options: CollectionFieldPathResolveOptions<TTerminal> = {},
): ResolvedCollectionFieldPathSegment[] | null {
	if (selectedPath.length === 0) return null;

	const { includePrimitiveFields, includeConstrainedFields, includeListRelationFields, includeListRelationTerminalFields } = resolveIncludeFlags(options);
	const includeRelationFields = options.includeRelationFields ?? true;
	const includeSystemCollections = options.includeSystemCollections ?? true;
	const terminal = options.terminal ?? 'primitive';
	let currentCollection = collection;
	const segments: ResolvedCollectionFieldPathSegment[] = [];

	for (const [index, fieldId] of selectedPath.entries()) {
		const isTerminal = index === selectedPath.length - 1;
		const primitiveField = currentCollection.primitiveFields.find((field) => field.id === fieldId);

		if (primitiveField) {
			if (!includePrimitiveFields) return null;
			if (options.includeField?.(currentCollection, primitiveField) === false) return null;
			if (!isTerminal) return null;
			if (!includeConstrainedFields && isConstrainedPrimitiveField(primitiveField)) return null;

			segments.push({ collection: currentCollection, field: primitiveField });
			return segments;
		}

		const relationField = currentCollection.relationFields?.find((field) => field.id === fieldId);

		if (!relationField) return null;
		if (options.includeField?.(currentCollection, relationField) === false) return null;
		if (!includeRelationFields) return null;

		if (isTerminal) {
			if (!isRelationSelectableTerminal(relationField, { terminal, includeListRelationTerminalFields })) return null;
			if (!includeSystemCollections && isSystemCollection(resolveOppositeCollection(relationField, options))) return null;

			segments.push({ collection: currentCollection, field: relationField });
			return segments;
		}

		if (!isRelationTraversable(relationField, { includeListRelationFields })) return null;

		const oppositeCollection = resolveOppositeCollection(relationField, options);

		if (!oppositeCollection) return null;
		if (!includeSystemCollections && isSystemCollection(oppositeCollection)) return null;

		segments.push({ collection: currentCollection, field: relationField });
		currentCollection = oppositeCollection;
	}

	return null;
}

export function resolveCollectionFieldPathSelection<TTerminal extends CollectionFieldPathTerminal | undefined = 'primitive'>(
	collection: CollectionFieldPathCollection,
	selectedPath: string[],
	options: CollectionFieldPathResolveOptions<TTerminal> = {},
): CollectionFieldPathForTerminal<TTerminal> | null {
	return walkCollectionFieldPath(collection, selectedPath, options)
		? selectedPath as CollectionFieldPathForTerminal<TTerminal>
		: null;
}

export function fieldIdToCollectionFieldPath(fieldId: GlobalPrimitiveFieldId | GlobalSingleRelationFieldId): CollectionFieldPath {
	return [fieldId] as CollectionFieldPath;
}

export function toCollectionFieldPathKey(fieldPath: CollectionFieldPath): string {
	return fieldPath.join('.');
}

export function resolveCollectionFieldPath(
	collection: CollectionFieldPathCollection,
	fieldPath: CollectionFieldPath,
	overrides?: Partial<CollectionFieldPathResolveOptions<'primitive-or-relation'>>,
): ResolvedCollectionFieldPathSegment[] | null {
	return walkCollectionFieldPath(collection, fieldPath, {
		includeConstrainedFields: true,
		includeListRelationFields: false,
		includeListRelationTerminalFields: true,
		terminal: 'primitive-or-relation',
		...overrides,
	});
}

export function getCollectionFieldPathLabels(
	collection: CollectionFieldPathCollection,
	fieldPath: CollectionFieldPath,
	getFieldDisplayName: (field: CollectionFieldPathField) => string,
): string[] {
	// Labels are purely cosmetic — resolve through list relations so paths persisted before the
	// list-relation guard still render with human names instead of raw field ids.
	const segments = resolveCollectionFieldPath(collection, fieldPath, { includeListRelationFields: true });

	if (!segments) return fieldPath;

	return segments.map(({ field }) => getFieldDisplayName(field));
}

export function getCollectionFieldPathLabel(
	collection: CollectionFieldPathCollection,
	fieldPath: CollectionFieldPath,
	getFieldDisplayName: (field: CollectionFieldPathField) => string,
): string {
	return getCollectionFieldPathLabels(collection, fieldPath, getFieldDisplayName).join(' / ');
}

/**
 * Yields every selectable field path reachable from `collection`, depth-first and cycle-safe
 * (a per-branch `visited` set stops a path re-entering a collection it already contains, bounded
 * by `maxDepth`). Lazy by design: callers that only need the first match can stop pulling early.
 */
function* walkCollectionFieldPaths(
	collection: CollectionFieldPathCollection,
	options: CollectionFieldPathListOptions<CollectionFieldPathTerminal> = {},
): Generator<CollectionFieldPath> {
	const { includePrimitiveFields, includeConstrainedFields, includeListRelationFields, includeListRelationTerminalFields } = resolveIncludeFlags(options);
	const includeSystemCollections = options.includeSystemCollections ?? true;
	const terminal: CollectionFieldPathTerminal = options.terminal ?? 'primitive-or-single-relation';
	const maxDepth = options.maxDepth ?? DEFAULT_FIELD_PATH_MAX_DEPTH;
	const terminalOptions = { terminal, includeListRelationTerminalFields };

	function* walk(
		currentCollection: CollectionFieldPathCollection,
		prefix: GlobalSingleRelationFieldId[],
		visited: Set<GlobalCollectionId>,
	): Generator<CollectionFieldPath> {
		if (prefix.length >= maxDepth) return;

		if (includePrimitiveFields) {
			for (const field of currentCollection.primitiveFields) {
				if (!includeConstrainedFields && isConstrainedPrimitiveField(field)) continue;
				if (options.includeField?.(currentCollection, field) === false) continue;
				yield [...prefix, field.id] as CollectionFieldPath;
			}
		}

		for (const field of currentCollection.relationFields ?? []) {
			if (options.includeField?.(currentCollection, field) === false) continue;
			if (!includeSystemCollections && isSystemCollection(field.oppositeCollection)) continue;

			if (isRelationSelectableTerminal(field, terminalOptions)) {
				yield [...prefix, field.id] as CollectionFieldPath;
			}

			if (!isRelationTraversable(field, { includeListRelationFields })) continue;

			const oppositeCollection = field.oppositeCollection;

			if (!oppositeCollection) continue;
			if (visited.has(oppositeCollection.id)) continue;

			yield* walk(oppositeCollection, [...prefix, field.id], new Set([...visited, oppositeCollection.id]));
		}
	}

	yield* walk(collection, [], new Set([collection.id]));
}

export function listCollectionFieldPaths(
	collection: CollectionFieldPathCollection,
	options: CollectionFieldPathListOptions<CollectionFieldPathTerminal> = {},
): CollectionFieldPath[] {
	return [...walkCollectionFieldPaths(collection, options)];
}

export function hasAnyHiddenCollectionFieldPath(
	collection: CollectionFieldPathCollection,
	isHidden: (fieldPath: CollectionFieldPath) => boolean,
	options: CollectionFieldPathListOptions<CollectionFieldPathTerminal> = {},
): boolean {
	for (const fieldPath of walkCollectionFieldPaths(collection, options)) {
		if (isHidden(fieldPath)) return true;
	}

	return false;
}

/** Candidates collected per returned result, so ranking can choose among more than the first matches. */
const SEARCH_CANDIDATES_PER_RESULT = 4;

/** Path checks allowed per candidate before a search gives up on filters that reject most paths. */
const SEARCH_CHECKS_PER_CANDIDATE = 20;

/**
 * Searches the collection graph before materializing paths. As with flattenWithPaths, depth
 * counts relation hops: zero searches the root fields, one also searches their immediate children.
 *
 * Exact leaf labels/API names are collected before prefix matches, so a wide branch cannot
 * crowd them out before ranking. Each pass returns a bounded sample, preferring shorter paths.
 * Arbitrary path filters cannot be evaluated on the graph; their candidate checks are also
 * bounded per pass. Use includeField for exclusions that can prune entire collection branches.
 */
export function searchCollectionFieldPaths(
	collection: CollectionFieldPathCollection,
	options: CollectionFieldPathBuildOptions,
	query: string,
	maxSearchDepth: number,
	limit = 50,
): FlattenedItem[] {
	const queryTokens = normalizeCollectionFieldPathQuery(query);
	const resultLimit = Math.max(0, Math.floor(limit));

	if (queryTokens.length === 0 || resultLimit === 0) return [];

	const resolved = resolveBuildOptions(options);
	const { filter } = resolved;
	const graph = filter?.graph ?? createUnfilteredGraph(resolved);
	// A filtered menu stops expanding relations at maxDepth, so its paths have at most maxDepth - 1 hops.
	const maxHops = Math.max(0, filter ? Math.min(maxSearchDepth, resolved.maxDepth - 1) : maxSearchDepth);
	const candidateLimit = resultLimit * SEARCH_CANDIDATES_PER_RESULT;
	const candidateCheckLimit = candidateLimit * SEARCH_CHECKS_PER_CANDIDATE;

	function collect(query: CollectionFieldPathGraphQuery, excludedPaths?: ReadonlySet<string>): CollectionFieldPathFlattenedItem[] {
		const candidates: CollectionFieldPathFlattenedItem[] = [];
		let checks = 0;

		graph.findPaths(collection, query, maxHops, (path) => {
			const pathIds = path.ids;
			if (excludedPaths?.size && excludedPaths.has(JSON.stringify(pathIds))) return true;
			checks++;

			// The graph admits a superset of fuzzy matches. Ranking re-applies the exact matcher, but
			// checking here keeps false positives from using up the candidate budget.
			const matches = filter?.accepts(pathIds) !== false
				&& fieldPathMatchPolicies[query.mode].acceptsPath(queryTokens, path.items);

			if (matches) {
				candidates.push({
					item: path.leaf,
					path: path.items,
					pathIds,
					pathLabels: path.items.map((item) => item.label),
					searchText: path.items.map((item) => item.searchText ?? item.label).join(' '),
				});
			}

			return candidates.length < candidateLimit && checks < candidateCheckLimit;
		});

		return candidates;
	}

	const exactCandidates = collect({ tokens: queryTokens, mode: FieldPathMatchMode.ExactLeaf });
	const exactResults = searchCollectionFieldPathItems(exactCandidates, query).slice(0, resultLimit);
	if (exactResults.length === resultLimit) return exactResults;

	const exactPaths = new Set(exactCandidates.map((item) => JSON.stringify(item.pathIds)));
	const strictCandidates = collect({ tokens: queryTokens, mode: FieldPathMatchMode.Prefix }, exactPaths);
	const candidates = exactResults.length > 0 || strictCandidates.length > 0
		? strictCandidates
		: collect({ tokens: queryTokens, mode: FieldPathMatchMode.Fuzzy });
	return [...exactResults, ...searchCollectionFieldPathItems(candidates, query)].slice(0, resultLimit);
}
